import { throwError } from "@/lib/throwError";
import { del, get, post, patch } from "@/services/axiosUitils";
import { DynamicPayload } from "@/utils/types/dynamicModule";

const basePath = "/formily/dynamic-model";

/** Spaces / special chars in model names must be encoded (e.g. "ServiceDesk Ticket"). */
function dynModelSeg(modelName: string): string {
  if (!modelName?.trim()) {
    throwError("Model name is required", "DynamicModuleService");
  }
  return encodeURIComponent(modelName.trim());
}

function mergeGridQueryParams(
  page: number,
  pageSize: number,
  extra?: Record<string, unknown>
): Record<string, string | number | boolean> {
  const params: Record<string, string | number | boolean> = {
    page,
    page_size: pageSize,
  };
  if (!extra) {
    return params;
  }
  for (const [k, v] of Object.entries(extra)) {
    if (v === undefined || v === null || v === "") {
      continue;
    }
    params[k] =
      typeof v === "boolean" ? v : typeof v === "number" ? v : String(v);
  }
  return params;
}

class DynamicModuleService {
  /**
   * Full grid payload: `{ grid_data, count?, page?, … }` as returned by the API.
   */
  getGridData = async (
    modelName: string,
    page: number = 1,
    pageSize: number = 50,
    /** Merged into the query string (e.g. `{ requester: userUuid }`). */
    filterParams?: Record<string, unknown>
  ): Promise<Record<string, unknown> | undefined> => {
    if (!modelName) {
      throwError("Model name is required", "DynamicModuleService");
    }
    return await get(`${basePath}/${dynModelSeg(modelName)}/grid_data/`, {
      params: mergeGridQueryParams(page, pageSize, filterParams),
    });
  };

  /**
   * Paginated list on the model collection URL (PTS-style):
   * `GET /formily/dynamic-model/{name}/?page=&page_size=` — same shape as browser/curl,
   * not the `grid_data/` subpath.
   */
  getModelListPage = async (
    modelName: string,
    page: number = 1,
    pageSize: number = 50,
    filterParams?: Record<string, unknown>
  ): Promise<Record<string, unknown> | undefined> => {
    if (!modelName) {
      throwError("Model name is required", "DynamicModuleService");
    }
    return await get(`${basePath}/${dynModelSeg(modelName)}/`, {
      params: mergeGridQueryParams(page, pageSize, filterParams),
    });
  };

  getGridColItems = async (
    modelName: string,
    page: number,
    pageSize: number,
    filterParams?: Record<string, unknown>
  ): Promise<any> => {
    if (!modelName) {
      throwError("Model name is required", "DynamicModuleService");
    }
    const response = await get(
      `${basePath}/${dynModelSeg(modelName)}/grid_data/`,
      {
        params: mergeGridQueryParams(page, pageSize, filterParams),
      }
    );
    return response?.grid_data ?? [];
  };

  getGridCoByConfig = async (
    modelName: string,
    page: number,
    pageSize: number,
    columns: string[],
    paramsFilter: any
  ): Promise<any> => {
    if (!modelName) {
      throwError("Model name is required", "DynamicModuleService");
    }
    const response = await get(`${basePath}/${dynModelSeg(modelName)}/`, {
      params: {
        offset: (page - 1) * pageSize,
        limit: pageSize,
        columns: JSON.stringify(columns),
        ...paramsFilter,
      },
    });
    return response;
  };

  getItemById = async (modelName: string, id: string): Promise<any> => {
    if (!modelName || !id) {
      throwError("Model name and ID are required", "DynamicModuleService");
    }
    return await get(
      `${basePath}/${dynModelSeg(modelName)}/${encodeURIComponent(id)}/`
    );
  };

  createItem = async (
    modelName: string,
    payload: DynamicPayload
  ): Promise<any> => {
    if (!modelName || !payload) {
      throwError("Model name and payload are required", "DynamicModuleService");
    }
    return await post(`${basePath}/${dynModelSeg(modelName)}/`, payload);
  };

  updateItem = async (
    modelName: string,
    id: string,
    payload: DynamicPayload
  ): Promise<any> => {
    if (!modelName || !id || !payload) {
      throwError(
        "Model name, ID, and payload are required",
        "DynamicModuleService"
      );
    }
    return await patch(
      `${basePath}/${dynModelSeg(modelName)}/${encodeURIComponent(id)}/`,
      payload
    );
  };

  deleteItem = async (modelName: string, id: string): Promise<any> => {
    if (!modelName || !id) {
      throwError("Model name and ID are required", "DynamicModuleService");
    }
    return await del(
      `${basePath}/${dynModelSeg(modelName)}/${encodeURIComponent(id)}/`
    );
  };

  bulkDeleteItems = async (modelName: string, ids: string[]): Promise<any> => {
    if (!modelName || !ids) {
      throwError("Model name and IDs are required", "DynamicModuleService");
    }
    return await post(`${basePath}/${dynModelSeg(modelName)}/bulk-delete/`, {
      ids,
    });
  };

  getDropDownData = async (modelName: string): Promise<any> => {
    if (!modelName) {
      throwError("Model name is required", "DynamicModuleService");
    }
    return await get(`${basePath}/${dynModelSeg(modelName)}/drop_down_data/`);
  };
}

const instance = new DynamicModuleService();
export default instance;
